import React, { createContext, useContext, useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { HashRouter, useLocation, useNavigate } from 'react-router-dom';
import { initialTiles, calculateTotalPixels } from '../utils/mockData';
import { useAuth } from '../hooks/useAuth';
import {
  loadTilesFromDb, upsertTileInDb, upsertManyTilesInDb,
  deleteTileFromDb, deleteAllTilesFromDb,
} from '../utils/tilesDb';
import { getReferralCodeFromUrl, storeReferralCode, recordReferralClick } from '../utils/affiliate';

const AppContext = createContext();
const LS_KEY = 'pixel_grid_tiles_v1';

const pageFromPath = (pathname) => {
  const page = pathname.replace('/', '').replace('#', '');
  return ['rankings', 'about', 'privacy', 'terms', 'admin', 'myblocks', 'affiliate'].includes(page) ? page : 'home';
};

const lsLoad = () => {
  try {
    const saved = localStorage.getItem(LS_KEY);
    if (saved) { const parsed = JSON.parse(saved); if (Array.isArray(parsed) && parsed.length > 0) return parsed; }
  } catch (e) {}
  return initialTiles;
};

const lsSave = (tiles) => { try { localStorage.setItem(LS_KEY, JSON.stringify(tiles)); } catch (e) {} };

export const AppProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, session, loading: authLoading, signInWithGoogle, signOut } = useAuth();

  const [tiles, setTilesRaw] = useState(initialTiles);
  const [dbReady, setDbReady] = useState(false);
  const [mode, setMode] = useState('pan');
  const [selection, setSelection] = useState(null);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [lastPurchase, setLastPurchase] = useState(null);
  const [liveVisitors] = useState(Math.floor(Math.random() * 80) + 120);
  const [currentPage, setCurrentPageState] = useState(() => pageFromPath(location.pathname));
  const [focusTileId, setFocusTileId] = useState(null);

  // Handle referral code on mount
  useEffect(() => {
    const code = getReferralCodeFromUrl();
    if (code) { storeReferralCode(code); recordReferralClick(code); }
  }, []);

  // Load tiles from Supabase on mount
  useEffect(() => {
    loadTilesFromDb().then((dbTiles) => {
      if (dbTiles && dbTiles.length > 0) { setTilesRaw(dbTiles); lsSave(dbTiles); }
      else { setTilesRaw(lsLoad()); }
      setDbReady(true);
    });
  }, []);

  const pendingUpserts = useRef([]);
  const setTiles = useCallback((updater) => {
    setTilesRaw((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      lsSave(next);
      pendingUpserts.current = next;
      return next;
    });
  }, []);

  const dbSyncTimer = useRef(null);
  useEffect(() => {
    if (!dbReady) return;
    clearTimeout(dbSyncTimer.current);
    dbSyncTimer.current = setTimeout(() => {
      if (pendingUpserts.current.length > 0) {
        upsertManyTilesInDb(pendingUpserts.current);
        pendingUpserts.current = [];
      }
    }, 800);
  }, [tiles, dbReady]);

  const pixelsSold = useMemo(() => calculateTotalPixels(tiles), [tiles]);

  useEffect(() => { setCurrentPageState(pageFromPath(location.pathname)); }, [location.pathname]);

  const setCurrentPage = useCallback((page) => {
    const nextPage = page || 'home';
    setCurrentPageState(nextPage);
    navigate(nextPage === 'home' ? '/' : `/${nextPage}`);
  }, [navigate]);

  const addTile = useCallback((tileData) => {
    const newTile = { ...tileData, id: Date.now().toString(), clicks: 0 };
    setTilesRaw((prev) => { const next = [...prev, newTile]; lsSave(next); return next; });
    upsertTileInDb(newTile);
    setLastPurchase(newTile);
    setSelection(null);
    setIsPurchaseModalOpen(false);
    setIsSuccessModalOpen(true);
  }, []);

  const updateTileField = useCallback((id, field, value) => {
    setTilesRaw((prev) => {
      const next = prev.map((t) => t.id === id ? { ...t, [field]: value } : t);
      lsSave(next);
      const updated = next.find((t) => t.id === id);
      if (updated) upsertTileInDb(updated);
      return next;
    });
  }, []);

  const removeTile = useCallback((id) => {
    setTilesRaw((prev) => { const next = prev.filter((t) => t.id !== id); lsSave(next); return next; });
    deleteTileFromDb(id);
  }, []);

  const clearAllTiles = useCallback(async () => {
    setTilesRaw([]); lsSave([]); await deleteAllTilesFromDb();
  }, []);

  const locateTileOnGrid = useCallback((tileId) => {
    setCurrentPage('home'); setFocusTileId(tileId);
  }, [setCurrentPage]);

  const value = {
    tiles, setTiles, pixelsSold, mode, setMode,
    selection, setSelection,
    isPurchaseModalOpen, setIsPurchaseModalOpen,
    isSuccessModalOpen, setIsSuccessModalOpen,
    isWelcomeModalOpen, setIsWelcomeModalOpen,
    isAuthModalOpen, setIsAuthModalOpen,
    addTile, updateTileField, removeTile, clearAllTiles,
    lastPurchase, liveVisitors,
    user, session, authLoading, signInWithGoogle, signOut,
    currentPage, setCurrentPage,
    focusTileId, setFocusTileId, locateTileOnGrid,
    dbReady,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = () => useContext(AppContext);